import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import type { CustomDefaultLeadViewSetting } from '@/api/openapi';
import { CustomDefaultLeadViewSettingApiService } from '@/api/user/resources/custom_default_lead_view_setting';
import { key } from '@/store/index';
import type { RootState } from '@/store/types';

const BC_DEFAULT_LEAD_LIST_COLUMNS = [
  'companyName',
  'targetName',
  'lastActivityAt',
  'lastActivityTypeText',
  'lastActivityComment',
  'nextActions',
  'callTargetCreatedAt',
  'operatorName',
] as const;

export const useDefaultLeadViewDisplayColumnsComposable = () => {
  const customDefaultSetting = ref<CustomDefaultLeadViewSetting | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const store = useStore<RootState>(key);

  const hasCustomDefaultSetting = computed(() => customDefaultSetting.value !== null);
  const defaultDisplayColumns = computed(() =>
    customDefaultSetting.value?.displayColumns
    ?? [...BC_DEFAULT_LEAD_LIST_COLUMNS],
  );

  const fetchDisplayColumns = async () => {
    try {
      const api = new CustomDefaultLeadViewSettingApiService();
      const response = await api.getCustomDefaultLeadViewSetting({
        errorHandlers: {
          disableDefaultErrorHandler: true,
        },
      });
      customDefaultSetting.value = response.data.customDefaultLeadViewSetting;
    } catch (_e) {
      customDefaultSetting.value = null;
    }
  };
  const updateDisplayColumns = async (columns: string[]): Promise<void> => {
    const api = new CustomDefaultLeadViewSettingApiService();

    await api.putCustomDefaultLeadViewSetting({
      request: {
        customDefaultLeadViewSettingBody: {
          customDefaultLeadViewSetting: {
            displayColumns: columns,
          },
        },
      },
      errorHandlers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        422: (error) => {
          throw error;
        },
      },
    });
    // 更新成功後、ローカル状態を更新
    customDefaultSetting.value = {
      displayColumns: columns,
    };
  };

  const setDefaultDisplayColumns = async () => {
    await fetchDisplayColumns();
    const columns = defaultDisplayColumns.value;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    store.commit('userUi/SET_LEAD_LIST_SELECTED_COLUMNS', columns);
  };

  return {
    defaultDisplayColumns,
    fetchDisplayColumns,
    setDefaultDisplayColumns,
    updateDisplayColumns,
    hasCustomDefaultSetting,
  };
};
